import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { useAppStore } from '../store/appStore';

/**
 * Replicates the legacy AngularJS topbar hide/show on scroll.
 * Also persists scroll position to settings (throttled) for "Continue Reading".
 */
export function useScrollTopBar() {
  const oldScrollRef = useRef(0);
  const scrollGapRef = useRef(0);
  const topbarHiddenRef = useRef(false);
  const scrollPersistTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const scrollPane = document.getElementById('scroll-center-pane-helper');
    if (!scrollPane) return;

    const SCROLL_GAP_THRESHOLD = 80;
    const isMobileViewport = () => window.innerWidth <= 768;

    const handleScroll = () => {
      const newScrollTop = scrollPane.scrollTop;

      if (scrollPersistTimer.current) clearTimeout(scrollPersistTimer.current);
      scrollPersistTimer.current = setTimeout(() => {
        useAppStore.getState().setSettings({ scroll_position: newScrollTop });
      }, 200);

      if (!isMobileViewport()) {
        oldScrollRef.current = newScrollTop;
        return;
      }

      const delta = newScrollTop - oldScrollRef.current;

      let gap = scrollGapRef.current + delta;
      if (gap > SCROLL_GAP_THRESHOLD) gap = SCROLL_GAP_THRESHOLD;
      else if (gap < 0) gap = 0;
      scrollGapRef.current = gap;

      const topBar = document.getElementById('top-bar');
      const recitationBtn = document.getElementById('recitation-btn');

      if (gap === SCROLL_GAP_THRESHOLD && !topbarHiddenRef.current) {
        topbarHiddenRef.current = true;
        if (topBar) {
          topBar.style.transition = 'transform 500ms ease';
          topBar.style.transform = 'translateY(-60px)';
        }
        if (recitationBtn) {
          recitationBtn.style.transition = 'transform 500ms ease';
          recitationBtn.style.transform = 'translateY(100px)';
        }
      } else if (gap === 0 && topbarHiddenRef.current) {
        topbarHiddenRef.current = false;
        if (topBar) {
          topBar.style.transition = 'transform 500ms ease';
          topBar.style.transform = 'translateY(0)';
        }
        if (recitationBtn) {
          recitationBtn.style.transition = 'transform 500ms ease';
          recitationBtn.style.transform = 'translateY(0)';
        }
      }

      oldScrollRef.current = newScrollTop;
    };

    scrollPane.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scrollPane.removeEventListener('scroll', handleScroll);
      if (scrollPersistTimer.current) clearTimeout(scrollPersistTimer.current);
    };
  }, []);
}
